import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { Activity } from './activity';
export { Activity };
import { activitySchema } from '@schemas/business-objects/activity';
import { ActivityItemFormArray } from './activity-item-form-group';
import { CurriculumFormGroup } from './curriculum-form-group';
import type { Resource } from './resource';
import type { TextBox } from './text-box';
import type { User } from './user';

export class ActivityFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    optional: SchemaFormControl;
    sequence: SchemaFormControl;
    locked: SchemaFormControl;
    curriculumId: SchemaFormControl;
    curriculum: CurriculumFormGroup;
    activityItems: ActivityItemFormArray;
  };

  declare value: Partial<Activity>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(activitySchema, Activity, data, options);
  }

  get object(): Activity {
    return new Activity(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'curriculum':
        return new CurriculumFormGroup();
      case 'activityItems':
        return new ActivityItemFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<Activity[]> {
    return Activity.find(filter);
  }

  static async findOne(filter?: any): Promise<Activity | null> {
    return Activity.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<Activity> {
    return Activity.findById(id, filter);
  }

  static async create(data: Partial<Activity>): Promise<Activity> {
    return Activity.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<Activity>,
  ): Promise<Activity> {
    return Activity.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return Activity.deleteById(id);
  }

  async dataForCopy(): Promise<any> {
    const instance = new Activity(this.getRawValue());
    return instance.dataForCopy();
  }
}

export class ActivityFormArray extends PersistedFormArray {
  readonly entityName = 'Activity' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(activitySchema, Activity, options);
  }

  protected override createItemGroup() {
    return new ActivityFormGroup();
  }

  get array(): Activity[] {
    return this.controls.map((group: ActivityFormGroup) => group.object);
  }
}

export class ActivityPersistedArray extends PersistedArray<Activity> {
  readonly entityName = 'Activity' as const;

  constructor(options?: PersistedArrayOptions) {
    super(Activity, options);
  }
}
